import * as React from "react";
import { useWebHaptics } from "@/lib/haptics";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "./alert-dialog";
import { BrandInput, BrandInputGroup } from "@/components/brand/brand-input";
import { BrandButton } from "@/components/brand/brand-button";

interface ConfirmDeleteDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	children?: React.ReactNode;
}

interface ConfirmDeleteDialogContentProps {
	children: React.ReactNode;
}

interface ConfirmDeleteDialogHeaderProps {
	title: string;
	description: string;
	icon?: React.ReactNode;
}

interface ConfirmDeleteDialogConfirmationProps {
	value: string;
	onChange: (value: string) => void;
	confirmText: string;
	label?: string;
	placeholder?: string;
	showCopyButton?: boolean;
}

interface ConfirmDeleteDialogFooterProps {
	onCancel: () => void;
	onConfirm: () => void;
	confirmDisabled?: boolean;
	isLoading?: boolean;
	cancelLabel?: string;
	confirmLabel?: string;
}

const ConfirmDeleteDialog = ({
	open,
	onOpenChange,
	children,
}: ConfirmDeleteDialogProps) => {
	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			{children}
		</AlertDialog>
	);
};

const ConfirmDeleteDialogTrigger = AlertDialogTrigger;

const ConfirmDeleteDialogContent = ({
	children,
}: ConfirmDeleteDialogContentProps) => {
	return <AlertDialogContent>{children}</AlertDialogContent>;
};

const ConfirmDeleteDialogHeader = ({
	title,
	description,
	icon,
}: ConfirmDeleteDialogHeaderProps) => {
	return (
		<AlertDialogHeader>
			<div className="flex items-start gap-3">
				<div
					className="flex shrink-0 items-center justify-center w-10 h-10"
					style={{ backgroundColor: "rgba(255,110,132,0.1)", borderLeft: "3px solid #ff6e84" }}
				>
					{icon || <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "20px", color: "#ff6e84", fontVariationSettings: "'FILL' 1" }}>warning</span>}
				</div>
				<div className="flex-1">
					<AlertDialogTitle className="text-lg" style={{ color: "#ff6e84" }}>
						{title}
					</AlertDialogTitle>
					<AlertDialogDescription className="mt-1 text-left">
						{description}
					</AlertDialogDescription>
				</div>
			</div>
		</AlertDialogHeader>
	);
};

const ConfirmDeleteDialogConfirmation = ({
	value,
	onChange,
	confirmText,
	label,
	placeholder,
	showCopyButton = false,
}: ConfirmDeleteDialogConfirmationProps) => {
	const [copied, setCopied] = React.useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(confirmText);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const displayLabel = label || (
		<>
			Type{" "}
			<span className="inline-flex items-center gap-1 pb-2">
				<span className="font-bold" style={{ color: "var(--km-on-surface)" }}>"{confirmText}"</span>
				{showCopyButton && (
					<button
						type="button"
						onClick={handleCopy}
						className="p-1 transition-colors min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{ color: copied ? "#2b9a66" : "#adaaaa" }}
						aria-label={copied ? "Copied!" : "Copy to clipboard"}
					>
						{copied
							? <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", fontVariationSettings: "'FILL' 1" }}>check_circle</span>
							: <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px" }}>content_copy</span>
						}
					</button>
				)}
			</span>{" "}
			to confirm
		</>
	);

	return (
		<BrandInputGroup>
			<label className="text-sm font-bold" style={{ color: "var(--km-on-surface-muted)" }}>
				{displayLabel}
			</label>
			<BrandInput
				autoFocus
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder || `Type ${confirmText}`}
				variant="error"
				className="border-red-200 focus-visible:ring-red-500 focus-visible:border-red-500"
			/>
		</BrandInputGroup>
	);
};

const ConfirmDeleteDialogFooter = ({
	onCancel,
	onConfirm,
	confirmDisabled = false,
	isLoading = false,
	cancelLabel,
	confirmLabel,
}: ConfirmDeleteDialogFooterProps) => {
	const { trigger } = useWebHaptics();
	const resolvedCancelLabel = cancelLabel ?? "Cancel";
	const resolvedConfirmLabel = confirmLabel ?? "Delete";

	return (
		<AlertDialogFooter>
			<BrandButton
				type="button"
				variant="secondary"
				onClick={onCancel}
				disabled={isLoading}
			>
				{resolvedCancelLabel}
			</BrandButton>
			<BrandButton
				type="button"
				variant="destructive"
				onClick={() => {
					trigger("success");
					onConfirm();
				}}
				disabled={confirmDisabled || isLoading}
			>
				{isLoading ? (
					<>
						<span className="material-symbols-outlined animate-spin mr-2" aria-hidden="true" style={{ fontSize: "16px" }}>progress_activity</span>
						Deleting...
					</>
				) : (
					resolvedConfirmLabel
				)}
			</BrandButton>
		</AlertDialogFooter>
	);
};

export {
	ConfirmDeleteDialog,
	ConfirmDeleteDialogTrigger,
	ConfirmDeleteDialogContent,
	ConfirmDeleteDialogHeader,
	ConfirmDeleteDialogConfirmation,
	ConfirmDeleteDialogFooter,
};
